import type { WhiteListedPlateCreateRequest } from '@arhs-fleetback/types/entities/white-listed-plates';

import { WHITELIST_API_URL, WHITELIST_FILTER_URL, WHITELIST_EXIST_URL } from 'constants/ApiConstants';
import { PaginationType } from 'constants/Global';

import type { MetaAction } from 'modules/global/generic.types';

import * as types from './actionTypes';
import type {
  FetchWhitelistAction,
  FetchWhitelistParams,
  CreateWhitelistAction,
  WhiteListedPlateUpdatePayload,
  UpdateWhitelistAction,
  DeleteWhitelistAction,
  CheckPlateExistAction,
  CheckPlateExistParams,
  SetWhitelistFilterAction,
  ClearWhitelistFiltersAction,
  OpenWhitelistModalAction,
  CloseWhitelistModalAction,
  WhiteListedPlateFormValues
} from './types';

export const fetchWhitelist = (params: FetchWhitelistParams, meta: MetaAction = {}): FetchWhitelistAction => ({
  type: types.FETCH_WHITELIST,
  payload: {
    request: {
      method: 'GET',
      url: WHITELIST_FILTER_URL,
      params: { ...params, projection: 'page', paginationType: PaginationType.SLICE }
    }
  },
  meta
});

export const createWhitelistEntry = (
  entry: WhiteListedPlateCreateRequest,
  meta: MetaAction = {}
): CreateWhitelistAction => ({
  type: types.CREATE_WHITELIST,
  payload: {
    request: {
      method: 'POST',
      url: WHITELIST_API_URL,
      data: entry
    }
  },
  meta
});

export const updateWhitelistEntry = (
  { id, plate, description }: WhiteListedPlateUpdatePayload,
  meta: MetaAction = {}
): UpdateWhitelistAction => ({
  type: types.UPDATE_WHITELIST,
  payload: {
    request: {
      method: 'PUT',
      url: `${WHITELIST_API_URL}/${id}`,
      data: { plate, description }
    }
  },
  meta
});

/**
 * Creates or updates, depending on whether the form carries the id of an existing plate. The branch
 * lives here because a caller cannot pick the creator by ternary: calling a union of functions
 * demands the *intersection* of their parameters, which no single form value satisfies.
 */
export const saveWhitelistEntry = (
  entry: WhiteListedPlateFormValues,
  meta: MetaAction = {}
): CreateWhitelistAction | UpdateWhitelistAction =>
  ('id' in entry ? updateWhitelistEntry(entry, meta) : createWhitelistEntry(entry, meta));

export const deleteWhitelistEntry = (entityId: string, meta: MetaAction = {}): DeleteWhitelistAction => ({
  type: types.DELETE_WHITELIST,
  payload: {
    entityId,
    request: {
      method: 'DELETE',
      url: `${WHITELIST_API_URL}/${entityId}`
    }
  },
  meta
});

export const checkPlateExist = (params: CheckPlateExistParams, meta: MetaAction = {}): CheckPlateExistAction => ({
  type: types.CHECK_PLATE_EXIST,
  payload: {
    request: {
      method: 'HEAD',
      url: WHITELIST_EXIST_URL,
      params
    }
  },
  meta: {
    '@app-request': true,
    ...meta
  }
});

export const setDashboardFilter = (field: string, value: unknown, meta: MetaAction = {}): SetWhitelistFilterAction => ({
  type: types.SET_FILTER,
  payload: { field, value },
  meta
});

export const clearDashboardFilters = (meta: MetaAction = {}): ClearWhitelistFiltersAction => ({
  type: types.CLEAR_FILTERS,
  meta
});

export const openModal = (entityId?: string, meta: MetaAction = {}): OpenWhitelistModalAction => ({
  type: types.OPEN_MODAL,
  payload: entityId,
  meta
});

export const closeModal = (meta: MetaAction = {}): CloseWhitelistModalAction => ({
  type: types.CLOSE_MODAL,
  meta
});
